import { useState, useEffect } from "react";
import { calculateCVCropPoints } from "../utils/cvCropCalculation";

export type Point = [number, number];

interface CropDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Converts percentage-based crop points to absolute pixel values.
 * @param {Array<[number, number]>} points - Array of [x, y] percentage points.
 * @param {number} imageWidth - The natural width of the source image.
 * @param {number} imageHeight - The natural height of the source image.
 * @returns {{x: number, y: number, width: number, height: number} | null}
 */
function convertPercentagesToPixels(
  points: Point[],
  imageWidth: number,
  imageHeight: number,
): CropDimensions | null {
  if (!points || points.length === 0) return null;

  // Extract all x and y coordinates
  const xCoords = points.map((p: Point) => p[0]);
  const yCoords = points.map((p: Point) => p[1]);

  // Find the min and max for both axes
  const minX_percent = Math.min(...xCoords);
  const maxX_percent = Math.max(...xCoords);
  const minY_percent = Math.min(...yCoords);
  const maxY_percent = Math.max(...yCoords);

  // Calculate width and height in percentages
  const width_percent = maxX_percent - minX_percent;
  const height_percent = maxY_percent - minY_percent;

  // Convert to absolute pixel values and round them to avoid sub-pixel issues
  return {
    x: Math.round(minX_percent * imageWidth),
    y: Math.round(minY_percent * imageHeight),
    width: Math.round(width_percent * imageWidth),
    height: Math.round(height_percent * imageHeight),
  };
}

interface CropResult {
  croppedImageUrl: string | null;
  cropDimensions: CropDimensions | null;
  originalDimensions: { width: number; height: number } | null;
  cropPoints: Point[] | null;
  crop?: boolean;
}

/**
 * A React hook to crop an image based on an array of percentage points.
 * @param {string} imageSrc - The URL of the source image.
 * @param {Array<[number, number]>} cropPoints - The crop area defined by percentage points.
 * @returns {CropResult} Object containing the cropped image URL, crop dimensions, and original dimensions.
 */
export function useImageCrop(
  imageSrc: string,
  points: { points: Point[] }[],
  cropPoints?: Point[],
  crop: boolean = false,
): CropResult {
  const [cropResult, setCropResult] = useState<CropResult>({
    croppedImageUrl: null,
    cropDimensions: null,
    originalDimensions: null,
    cropPoints: null,
  });
  useEffect(() => {
    // Ensure we have something to work with
    if (!imageSrc) {
      setCropResult({
        croppedImageUrl: null,
        cropDimensions: null,
        originalDimensions: null,
        cropPoints: null,
      });
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous"; // Important for CORS
    image.src = imageSrc;

    image.onload = () => {
      const originalDimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight,
      };

      const cropSize = crop ? 2 : 1;
      const calculatedCropPoints = calculateCVCropPoints(
        points,
        originalDimensions,
        cropSize,
      );

      const pixelCrop = convertPercentagesToPixels(
        cropPoints || calculatedCropPoints || [],
        image.naturalWidth,
        image.naturalHeight,
      );

      if (!pixelCrop || pixelCrop.width === 0 || pixelCrop.height === 0) {
        console.error("Invalid crop dimensions calculated.");
        return;
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0, // Draw the cropped section at the top-left of the new canvas
        pixelCrop.width,
        pixelCrop.height,
      );

      setCropResult({
        croppedImageUrl: canvas.toDataURL("image/jpeg"),
        cropDimensions: pixelCrop,
        originalDimensions,
        cropPoints: cropPoints || calculatedCropPoints,
      });
    };

    image.onerror = () => {
      console.error("Failed to load image for cropping.");
      setCropResult({
        croppedImageUrl: null,
        cropDimensions: null,
        originalDimensions: null,
        cropPoints: null,
      });
    };
  }, [imageSrc, cropPoints]); // Dependency array using cropPoints

  return cropResult;
}
